import {ref} from 'vue';
import axios from 'axios';

export interface PasskeyCredential {
    id: number;
    name: string;
    created_at: string;
}

export interface PublicKeyCredentialCreationOptionsJSON {
    challenge: string;
    rp: {
        name: string;
        id?: string;
    };
    user: {
        id: string;
        name: string;
        displayName: string;
    };
    pubKeyCredParams: Array<{
        type: string;
        alg: number;
    }>;
    timeout?: number;
    excludeCredentials?: Array<{
        id: string;
        type: string;
        transports?: AuthenticatorTransport[];
    }>;
    authenticatorSelection?: {
        authenticatorAttachment?: 'platform' | 'cross-platform';
        requireResidentKey?: boolean;
        residentKey?: 'discouraged' | 'preferred' | 'required';
        userVerification?: 'required' | 'preferred' | 'discouraged';
    };
    attestation?: 'none' | 'indirect' | 'direct' | 'enterprise';
}

export interface PublicKeyCredentialRequestOptionsJSON {
    challenge: string;
    timeout?: number;
    rpId?: string;
    allowCredentials?: Array<{
        id: string;
        type: string;
        transports?: AuthenticatorTransport[];
    }>;
    userVerification?: 'required' | 'preferred' | 'discouraged';
}

export function usePasskey() {
    // Check WebAuthn support (runtime-safe)
    const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';

    // Check WebAuthn support
    const checkSupport = () =>
        isBrowser &&
        'PublicKeyCredential' in window &&
        !!navigator.credentials;

    const isSupported = ref<boolean>(checkSupport());
    const isLoading = ref(false);
    const error = ref<string | null>(null);

    /**
     * Convert base64url to ArrayBuffer
     */
    const base64urlToBuffer = (base64url: string): ArrayBuffer => {
        if (!isBrowser) {
            throw new Error('base64urlToBuffer can only be used in a browser environment');
        }

        const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
        const padLen = (4 - (base64.length % 4)) % 4;
        const padded = base64 + '='.repeat(padLen);
        const binary = atob(padded);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
    };

    /**
     * Convert ArrayBuffer to base64url
     */
    const bufferToBase64url = (buffer: ArrayBuffer): string => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    };

    /**
     * Prepare credential creation options from the server
     */
    const prepareCreationOptions = (
        options: PublicKeyCredentialCreationOptionsJSON
    ): CredentialCreationOptions => {
        return {
            publicKey: {
                ...options,
                challenge: base64urlToBuffer(options.challenge),
                user: {
                    ...options.user,
                    id: base64urlToBuffer(options.user.id),
                },
                pubKeyCredParams: options.pubKeyCredParams.map((param) => ({
                    ...param,
                    type: param.type as PublicKeyCredentialType,
                })),
                excludeCredentials: options.excludeCredentials?.map((cred) => ({
                    ...cred,
                    id: base64urlToBuffer(cred.id),
                    type: cred.type as PublicKeyCredentialType,
                    transports: cred.transports as AuthenticatorTransport[] | undefined,
                })),
            },
        };
    };

    /**
     * Prepare credential request options from server
     */
    const prepareRequestOptions = (
        options: PublicKeyCredentialRequestOptionsJSON
    ): CredentialRequestOptions => {
        return {
            publicKey: {
                ...options,
                challenge: base64urlToBuffer(options.challenge),
                allowCredentials: options.allowCredentials?.map((cred) => ({
                    ...cred,
                    id: base64urlToBuffer(cred.id),
                    type: cred.type as PublicKeyCredentialType,
                    transports: cred.transports as AuthenticatorTransport[] | undefined,
                })),
            },
        };
    };

    /**
     * Register a new passkey
     */
    const registerPasskey = async (name: string): Promise<PasskeyCredential | null> => {
        if (!isSupported.value) {
            error.value = 'Passkeys are not supported in this browser';
            return null;
        }

        isLoading.value = true;
        error.value = null;

        try {
            // Get registration options from server
            const {data: optionsData} = await axios.post('/v1/passkeys/register/options');
            const options = prepareCreationOptions(optionsData.publicKey);

            // Create credential
            const credential = await navigator.credentials.create(options) as PublicKeyCredential;

            if (!credential) {
                throw new Error('Failed to create credential');
            }

            const response = credential.response as AuthenticatorAttestationResponse;

            // Send credential to server
            const {data} = await axios.post('/v1/passkeys/register', {
                name,
                id: credential.id,
                rawId: bufferToBase64url(credential.rawId),
                type: credential.type,
                response: {
                    clientDataJSON: bufferToBase64url(response.clientDataJSON),
                    attestationObject: bufferToBase64url(response.attestationObject),
                },
            });

            return data.key;
        } catch (err: unknown) {
            if (err instanceof Error) {
                if (err.name === 'NotAllowedError') {
                    error.value = 'Passkey registration was cancelled';
                } else if (err.name === 'InvalidStateError') {
                    error.value = 'This device already has a passkey registered';
                } else {
                    error.value = err.message || 'Failed to register passkey';
                }
            } else {
                error.value = 'An unknown error occurred';
            }
            return null;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Authenticate with a passkey
     */
    const authenticateWithPasskey = async (email?: string): Promise<{ access_token: string } | null> => {
        if (!isSupported.value) {
            error.value = 'Passkeys are not supported in this browser';
            return null;
        }

        isLoading.value = true;
        error.value = null;

        try {
            // Get authentication options from the server
            const {data: optionsData} = await axios.post('/v1/passkeys/auth/options', {
                email,
            });
            const options = prepareRequestOptions(optionsData.publicKey);

            // Get credential
            const credential = await navigator.credentials.get(options) as PublicKeyCredential;

            if (!credential) {
                throw new Error('Failed to get credential');
            }

            const response = credential.response as AuthenticatorAssertionResponse;

            // Send assertion to server
            const {data} = await axios.post('/v1/passkeys/auth', {
                id: credential.id,
                rawId: bufferToBase64url(credential.rawId),
                type: credential.type,
                response: {
                    clientDataJSON: bufferToBase64url(response.clientDataJSON),
                    authenticatorData: bufferToBase64url(response.authenticatorData),
                    signature: bufferToBase64url(response.signature),
                    userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
                },
            });

            return data;
        } catch (err: unknown) {
            if (err instanceof Error) {
                if (err.name === 'NotAllowedError') {
                    error.value = 'Authentication was cancelled';
                } else {
                    error.value = err.message || 'Failed to authenticate with passkey';
                }
            } else {
                error.value = 'An unknown error occurred';
            }
            return null;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Get all passkeys for the current user
     */
    const getPasskeys = async (): Promise<PasskeyCredential[]> => {
        try {
            const {data} = await axios.get('/v1/passkeys');
            return data.passkeys;
        } catch (err) {
            console.error(err);
            error.value = 'Failed to fetch passkeys';
            return [];
        }
    };

    /**
     * Delete a passkey
     */
    const deletePasskey = async (id: number): Promise<boolean> => {
        try {
            await axios.delete(`/v1/passkeys/${id}`);
            return true;
        } catch (err) {
            console.error(err);
            error.value = 'Failed to delete passkey';
            return false;
        }
    };

    /**
     * Update passkey name
     */
    const updatePasskeyName = async (id: number, name: string): Promise<boolean> => {
        try {
            await axios.put(`/v1/passkeys/${id}`, {name});
            return true;
        } catch (err) {
            console.error(err);
            error.value = 'Failed to update passkey name';
            return false;
        }
    };

    return {
        isSupported,
        isLoading,
        error,
        registerPasskey,
        authenticateWithPasskey,
        getPasskeys,
        deletePasskey,
        updatePasskeyName,
    };
}

